/**
 * CustomerList Page - T068
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { GlassPanel, Button, Input, EmptyState, LoadingSpinner, Pagination } from '../components/ui';
import { CustomerCard } from '../components/customers/CustomerCard';
import { useCustomerList, type CustomerListQuery } from '../hooks/useCustomers';

export const Route = createFileRoute('/customers')({
  component: CustomersPage,
});

function CustomersPage() {
  const [query, setQuery] = useState<CustomerListQuery>({
    page: 1,
    limit: 12,
    sortBy: 'companyName',
    sortOrder: 'asc',
    isArchived: false,
  });
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, error } = useCustomerList(query);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery({ ...query, q: searchInput, page: 1 });
  };

  const toggleArchived = () => {
    setQuery({ ...query, isArchived: !query.isArchived, page: 1 });
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white">Kunden</h1>
            <p className="text-white/50 mt-2">
              {data?.total ?? 0} Kunden{query.isArchived ? ' (archiviert)' : ''}
            </p>
          </div>
          <Link to="/customers/new">
            <Button>
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Neuer Kunde
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <GlassPanel className="mb-8">
          <div className="flex flex-wrap gap-4">
            <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
              <Input
                placeholder="Suchen..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                icon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                }
              />
            </form>

            <Button
              variant={query.isArchived ? 'primary' : 'secondary'}
              onClick={toggleArchived}
            >
              {query.isArchived ? 'Archiviert' : 'Aktiv'}
            </Button>
          </div>
        </GlassPanel>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <GlassPanel>
            <div className="text-center py-8">
              <p className="text-red-400">Fehler beim Laden der Kunden</p>
            </div>
          </GlassPanel>
        ) : data?.customers.length === 0 ? (
          <EmptyState
            title={query.q ? 'Keine Treffer' : 'Keine Kunden'}
            description={
              query.q
                ? 'Versuchen Sie eine andere Suche'
                : 'Erstellen Sie Ihren ersten Kunden'
            }
            action={
              !query.q
                ? {
                    label: 'Kunde erstellen',
                    onClick: () => {},
                  }
                : undefined
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
              {data?.customers.map((customer) => (
                <CustomerCard key={customer.id} customer={customer} />
              ))}
            </div>

            {data && data.totalPages > 1 && (
              <div className="flex justify-center">
                <Pagination
                  page={query.page ?? 1}
                  totalPages={data.totalPages}
                  onPageChange={(page) => setQuery({ ...query, page })}
                />
              </div>
            )}
          </>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
